import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  Autorenew as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import * as shootingScheduleService from '../../services/shootingScheduleService';
import * as callsheetService from '../../services/callsheetService';
import axios from '../../utils/axiosConfig';
import DayOutOfDaysReport from './DayOutOfDaysReport';


function DayOutOfDaysTab({ projectId, projectName = 'Production' }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null); // Single schedule per project
  const [error, setError] = useState(null);
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [printOption, setPrintOption] = useState('all'); // 'all' or 'week'
  const [selectedWeek, setSelectedWeek] = useState(1);

  // Auto-generate/update DOOD schedule when the component loads
  // This ensures the DOOD always reflects current data from oneliner, cast, etc.
  // But preserves manual edits (off days, status changes)
  useEffect(() => {
    if (!projectId) return;
    
    // Load saved manual edits before regenerating
    generateSchedule();
  }, [projectId]);

  // Load saved manual edits from localStorage
  const loadManualEdits = () => {
    try {
      const saved = localStorage.getItem(`dood-edits-${projectId}`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Could not load manual edits:', e);
    }
    return { offDays: [], manualStatuses: {} };
  };

  // Save manual edits to localStorage
  const saveManualEdits = (offDays, manualStatuses) => {
    try {
      localStorage.setItem(`dood-edits-${projectId}`, JSON.stringify({ offDays, manualStatuses }));
    } catch (e) {
      console.warn('Could not save manual edits:', e);
    }
  };

  const generateSchedule = async () => {
    setLoading(true);
    setError(null);
    
    // Load previously saved manual edits
    const savedEdits = loadManualEdits();
    
    try {
      // 1. Fetch project cast (characters/actors)
      let castMembers = [];
      try {
        const token = localStorage.getItem('token');
        const castRes = await axios.get(`/api/v1/cast/project/${projectId}`, 
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
        );
        if (Array.isArray(castRes.data)) {
          castMembers = castRes.data;
        }
      } catch (e) {
        console.warn('Could not fetch cast members:', e);
      }

      // 2. Fetch scenes and day breaks from one-liner to determine shooting days
      let shootingDays = [];
      let startDate = new Date();
      let onelinerScenes = [];
      let dayBreaks = [];
      
      // Get the shooting start date from localStorage (same as one-liner uses)
      const savedStartDate = localStorage.getItem(`oneliner_startDate_${projectId}`);
      if (savedStartDate) {
        // Parse YYYY-MM-DD format in local time to avoid timezone issues
        const [year, month, day] = savedStartDate.split('-').map(Number);
        startDate = new Date(year, month - 1, day);
        console.log('Using shooting start date from one-liner:', savedStartDate);
      } else {
        // Default to 2/4/2026 if no start date is set
        startDate = new Date(2026, 1, 4); // Month is 0-indexed, so 1 = February
        console.log('Using default start date: 2026-02-04');
      }
      
      // Helper to calculate date for a shooting day based on start date
      let nextSequentialDate = new Date(startDate);
      
      const getDateForDay = (dayNumber, customDateStr) => {
        if (customDateStr) {
          const [year, month, day] = customDateStr.split('-').map(Number);
          const customDate = new Date(year, month - 1, day);
          nextSequentialDate = new Date(customDate);
          nextSequentialDate.setDate(nextSequentialDate.getDate() + 1);
          return customDateStr;
        }
        const date = new Date(nextSequentialDate);
        const dateStr = date.toISOString().split('T')[0];
        nextSequentialDate.setDate(nextSequentialDate.getDate() + 1);
        return dateStr;
      };
      
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch scenes from one-liner
        const scenesRes = await axios.get(`/api/v1/scripts/project/${projectId}/scenes`, { headers });
        if (scenesRes.data?.scenes && Array.isArray(scenesRes.data.scenes)) {
          onelinerScenes = scenesRes.data.scenes;
          console.log('Fetched one-liner scenes:', onelinerScenes.length);
        }
        
        // Fetch day breaks from one-liner
        const breaksRes = await axios.get(`/api/v1/scripts/${projectId}/breaks`, { headers });
        if (Array.isArray(breaksRes.data)) {
          dayBreaks = breaksRes.data.filter(b => (b.type || '').toUpperCase() === 'DAY_BREAK');
          console.log('Fetched day breaks:', dayBreaks.length);
          console.log('Raw day breaks from API:', breaksRes.data.filter(b => (b.type || '').toUpperCase() === 'DAY_BREAK'));
        }
        
        // Build shooting days from scenes and day breaks
        if (onelinerScenes.length > 0) {
          // Sort day breaks by afterSceneIndex
          dayBreaks.sort((a, b) => (a.afterSceneIndex || 0) - (b.afterSceneIndex || 0));
          
          console.log('Day breaks with customDate:', dayBreaks.map(db => ({
            afterSceneIndex: db.afterSceneIndex,
            customDate: db.customDate,
            _id: db._id
          })));
          
          // Group scenes into shooting days based on day breaks
          // Each day break marks the END of a shooting day
          let currentDayScenes = [];
          let dayNum = 1;
          
          onelinerScenes.forEach((scene, idx) => {
            currentDayScenes.push(scene);
            
            // Check if there's a day break after this scene
            const breakAfter = dayBreaks.find(b => b.afterSceneIndex === idx);
            
            // Only create a shooting day if there's an explicit day break
            // Don't auto-create for last scene - only breaks define days
            if (breakAfter) {
              // End of this shooting day
              // Use customDate if set, otherwise calculate sequentially from current position
              const breakDate = getDateForDay(dayNum, breakAfter?.customDate);
              
              // Check if this date falls on a weekend (Saturday = 6, Sunday = 0)
              // Parse in local time to avoid timezone issues
              const [year, month, day] = breakDate.split('-').map(Number);
              const dateObj = new Date(year, month - 1, day);
              const dayOfWeek = dateObj.getDay();
              const isOffDay = (dayOfWeek === 0 || dayOfWeek === 6); // Sunday or Saturday
              
              // DEBUG: Log detailed info for 2/9
              if (breakDate === '2026-02-09') {
                console.log('*** DEBUG 2/9 ***');
                console.log('  breakDate:', breakDate);
                console.log('  dateObj:', dateObj);
                console.log('  dayOfWeek:', dayOfWeek, '(0=Sun, 1=Mon, 6=Sat)');
                console.log('  isOffDay:', isOffDay);
                console.log('*******************');
              }
              
              // Extract characters from all scenes in this day
              const dayCharacters = new Set();
              currentDayScenes.forEach(s => {
                const chars = s.characters || s.cast || [];
                if (chars instanceof Set) {
                  chars.forEach(c => dayCharacters.add(c));
                } else if (Array.isArray(chars)) {
                  chars.forEach(c => {
                    const name = typeof c === 'string' ? c : (c.name || c.characterName || '');
                    if (name) dayCharacters.add(name);
                  });
                }
              });
              
              shootingDays.push({
                day: dayNum,
                date: breakDate,
                isOff: isOffDay,
                scenes: currentDayScenes.map(s => ({
                  ...s,
                  characters: s.characters || s.cast || []
                })),
                characters: Array.from(dayCharacters)
              });
              
              console.log(`Day ${dayNum}: date=${breakDate}, isOff=${isOffDay}, scenes=${currentDayScenes.length}`);
              
              currentDayScenes = [];
              dayNum++;
            }
          });
          
          // Handle remaining scenes after last day break (if any)
          // NOTE: Commented out to prevent extra day beyond last break date
          // if (currentDayScenes.length > 0) {
          //   const breakDate = getDateForDay(dayNum, null);
          //   
          //   const [year, month, day] = breakDate.split('-').map(Number);
          //   const dateObj = new Date(year, month - 1, day);
          //   const dayOfWeek = dateObj.getDay();
          //   const isOffDay = (dayOfWeek === 0 || dayOfWeek === 6);
          //   
          //   const dayCharacters = new Set();
          //   currentDayScenes.forEach(s => {
          //     const chars = s.characters || s.cast || [];
          //     if (chars instanceof Set) {
          //       chars.forEach(c => dayCharacters.add(c));
          //     } else if (Array.isArray(chars)) {
          //       chars.forEach(c => {
          //         const name = typeof c === 'string' ? c : (c.name || c.characterName || '');
          //         if (name) dayCharacters.add(name);
          //       });
          //     }
          //   });
          //   
          //   shootingDays.push({
          //     day: dayNum,
          //     date: breakDate,
          //     isOff: isOffDay,
          //     scenes: currentDayScenes.map(s => ({
          //       ...s,
          //       characters: s.characters || s.cast || []
          //     })),
          //     characters: Array.from(dayCharacters)
          //   });
          //   
          //   console.log(`Day ${dayNum} (final): date=${breakDate}, isOff=${isOffDay}, scenes=${currentDayScenes.length}`);
          // }
          
          // Set start date from the first shooting day's date
          if (shootingDays.length > 0 && shootingDays[0].date) {
            startDate = new Date(shootingDays[0].date);
          }
          
          console.log('=== FINAL SHOOTING DAYS ===');
          shootingDays.forEach(sd => {
            console.log(`Day ${sd.day}: date=${sd.date}, scenes=${sd.scenes.length}`);
          });
          console.log('===========================');
          
          console.log('Built shooting days from one-liner:', shootingDays.map(sd => ({
            day: sd.day,
            date: sd.date,
            sceneCount: sd.scenes.length,
            characterCount: sd.characters?.length || 0
          })));
        }
      } catch (e) {
        console.warn('Could not fetch one-liner scenes/breaks:', e);
      }
      
      // Fallback: Try shooting schedule if no one-liner data
      if (shootingDays.length === 0) {
        try {
          const schedules = await shootingScheduleService.getShootingSchedules(projectId);
          if (Array.isArray(schedules) && schedules.length > 0) {
            const schedule = schedules[0];
            const scheduleId = schedule._id || schedule.id;
            if (scheduleId) {
              const fullSchedule = await shootingScheduleService.getShootingScheduleById(scheduleId);
              if (fullSchedule.shootingDays && Array.isArray(fullSchedule.shootingDays)) {
                shootingDays = fullSchedule.shootingDays;
              }
              if (fullSchedule.startDate) {
                startDate = new Date(fullSchedule.startDate);
              }
            }
          }
        } catch (e) {
          console.warn('Could not fetch shooting schedule:', e);
        }
      }

      // 3. If no cast found, try to fetch characters from screenplay
      if (castMembers.length === 0) {
        try {
          const token = localStorage.getItem('token');
          const scriptCharsRes = await axios.get(`/api/v1/scripts/project/${projectId}/characters`,
            token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
          );
          if (scriptCharsRes.data?.characters && Array.isArray(scriptCharsRes.data.characters)) {
            castMembers = scriptCharsRes.data.characters.map((name, idx) => ({
              _id: `script-char-${idx}`,
              name: name,
              characterName: name
            }));
            console.log('Fetched characters from screenplay:', castMembers.length);
          }
        } catch (e) {
          console.warn('Could not fetch characters from screenplay:', e);
        }
      }

      // 4. Try to get characters from callsheets (has detailed cast call times)
      let callsheetCharacters = new Set();
      try {
        const callsheets = await callsheetService.getProjectCallsheets(projectId);
        if (Array.isArray(callsheets) && callsheets.length > 0) {
          callsheets.forEach(cs => {
            // Extract from talent/cast section
            (cs.talent || cs.cast || []).forEach(talent => {
              const name = talent.character || talent.characterName || talent.role || '';
              if (name) callsheetCharacters.add(name.toUpperCase());
            });
            // Extract from scenes
            (cs.scenes || []).forEach(scene => {
              (scene.cast || scene.characters || []).forEach(char => {
                const name = typeof char === 'string' ? char : (char.name || char.characterName || '');
                if (name) callsheetCharacters.add(name.toUpperCase());
              });
            });
          });
          console.log('Found characters from callsheets:', callsheetCharacters.size);
        }
      } catch (e) {
        console.warn('Could not fetch callsheets:', e);
      }

      // 5. Extract characters from one-liner scenes (already fetched above)
      let onelinerCharacters = new Set();
      if (onelinerScenes.length > 0) {
        onelinerScenes.forEach(scene => {
          // Characters can be stored as array or Set
          const chars = scene.characters || scene.cast || [];
          if (Array.isArray(chars)) {
            chars.forEach(char => {
              const name = typeof char === 'string' ? char : (char.name || '');
              if (name) onelinerCharacters.add(name.toUpperCase());
            });
          }
        });
        console.log('Found characters from one-liner scenes:', onelinerCharacters.size);
      }

      // 6. Merge all character sources if no cast found
      if (castMembers.length === 0) {
        const allCharacters = new Set([
          ...callsheetCharacters,
          ...onelinerCharacters
        ]);
        
        // Also extract from shooting schedule scenes
        shootingDays.forEach(day => {
          (day.scenes || []).forEach(scene => {
            (scene.cast || scene.characters || []).forEach(char => {
              const name = typeof char === 'string' ? char : (char.name || char.characterName || '');
              if (name) allCharacters.add(name.toUpperCase());
            });
          });
        });

        castMembers = Array.from(allCharacters).map((name, idx) => ({
          _id: `extracted-${idx}`,
          name: name,
          characterName: name
        }));
        console.log('Merged characters from all sources:', castMembers.length);
      }

      // 5. Build character list for DOOD
      const characters = castMembers.map(c => ({
        id: c._id || c.id || `char-${Math.random().toString(36).substr(2, 9)}`,
        name: c.characterName || c.name || 'Unknown',
        role: c.role || c.actorName || ''
      }));

      console.log('=== DOOD Character List ===');
      console.log('Characters from castMembers:', characters.map(c => ({ id: c.id, name: c.name })));
      console.log('Characters extracted from shootingDays:', shootingDays.map(sd => ({
        day: sd.day,
        characters: sd.characters
      })));
      console.log('===========================');

      if (characters.length === 0) {
        setError('No characters found. Please ensure your project has: (1) Cast members added in the Cast tab, OR (2) A screenplay with character dialogue, OR (3) A shooting schedule with characters assigned to scenes.');
        setLoading(false);
        return;
      }

      // If no shooting schedule, create a simple 10-day schedule with all characters working
      if (shootingDays.length === 0) {
        console.log('No shooting schedule found, creating default schedule');
        const defaultDays = 10;
        for (let i = 0; i < defaultDays; i++) {
          shootingDays.push({
            day: i + 1,
            scenes: characters.map(c => ({ characters: [c.name] }))
          });
        }
      }

      // 7. Build character appearance map: { characterId: [dayNumbers where they appear] }
      // This uses data from one-liner scenes (primary), shooting schedule, and callsheets
      const characterAppearances = {};
      characters.forEach(c => { characterAppearances[c.id] = []; });

      // Helper to find character by name (case-insensitive)
      const findCharacterByName = (name) => {
        const upperName = (name || '').toUpperCase();
        return characters.find(c => (c.name || '').toUpperCase() === upperName);
      };

      // Primary: Use one-liner shooting days data (characters extracted from scenes per day)
      shootingDays.forEach((day) => {
        const dayNum = day.day;
        
        // Use the pre-extracted characters array from the shooting day
        if (day.characters && Array.isArray(day.characters)) {
          day.characters.forEach(charName => {
            const char = findCharacterByName(charName);
            if (char && !characterAppearances[char.id].includes(dayNum)) {
              characterAppearances[char.id].push(dayNum);
            }
          });
        }
        
        // Also check scenes for any missed characters
        (day.scenes || []).forEach(scene => {
          const sceneChars = scene.cast || scene.characters || [];
          if (sceneChars instanceof Set) {
            sceneChars.forEach(charName => {
              const char = findCharacterByName(charName);
              if (char && !characterAppearances[char.id].includes(dayNum)) {
                characterAppearances[char.id].push(dayNum);
              }
            });
          } else if (Array.isArray(sceneChars)) {
            sceneChars.forEach(c => {
              const name = typeof c === 'string' ? c : (c.name || c.characterName || '');
              const char = findCharacterByName(name);
              if (char && !characterAppearances[char.id].includes(dayNum)) {
                characterAppearances[char.id].push(dayNum);
              }
            });
          }
        });
      });
      
      console.log('Character appearances from one-liner:', 
        Object.entries(characterAppearances)
          .filter(([_, days]) => days.length > 0)
          .map(([id, days]) => ({ id, days }))
      );

      // Also use callsheet data to determine character appearances per shooting day
      try {
        const callsheets = await callsheetService.getProjectCallsheets(projectId);
        if (Array.isArray(callsheets) && callsheets.length > 0) {
          callsheets.forEach(cs => {
            const shootingDay = cs.shootingDay || cs.dayNumber;
            if (!shootingDay) return;
            
            // Get characters from talent section
            (cs.talent || cs.cast || []).forEach(talent => {
              const charName = talent.character || talent.characterName || talent.role || '';
              const char = findCharacterByName(charName);
              if (char && !characterAppearances[char.id].includes(shootingDay)) {
                characterAppearances[char.id].push(shootingDay);
              }
            });
            
            // Get characters from scenes
            (cs.scenes || []).forEach(scene => {
              (scene.cast || scene.characters || []).forEach(c => {
                const charName = typeof c === 'string' ? c : (c.name || c.characterName || '');
                const char = findCharacterByName(charName);
                if (char && !characterAppearances[char.id].includes(shootingDay)) {
                  characterAppearances[char.id].push(shootingDay);
                }
              });
            });
          });
          console.log('Enhanced appearances with callsheet data');
        }
      } catch (e) {
        console.warn('Could not enhance with callsheet data:', e);
      }

      // Sort appearances for each character
      Object.keys(characterAppearances).forEach(charId => {
        characterAppearances[charId].sort((a, b) => a - b);
      });

      // 6. Calculate DOOD statuses for each character on each day
      const calculateStatus = (charId, dayNum, totalDays) => {
        const appearances = characterAppearances[charId] || [];
        if (appearances.length === 0) return null;
        
        const firstDay = Math.min(...appearances);
        const lastDay = Math.max(...appearances);
        const isWorkingToday = appearances.includes(dayNum);
        
        // Before first day or after last day = no status
        if (dayNum < firstDay || dayNum > lastDay) return null;
        
        // Only works one day
        if (firstDay === lastDay && dayNum === firstDay) return 'SWF';
        
        if (isWorkingToday) {
          // Check for pickup after 7+ day gap
          const prevWorkDay = appearances.filter(d => d < dayNum).sort((a, b) => b - a)[0];
          const daysSinceLast = prevWorkDay ? (dayNum - prevWorkDay) : 0;
          
          // Check for drop before 7+ day gap
          const nextWorkDay = appearances.filter(d => d > dayNum).sort((a, b) => a - b)[0];
          const daysUntilNext = nextWorkDay ? (nextWorkDay - dayNum) : 0;
          
          if (dayNum === firstDay) {
            return 'SW'; // Start Work
          } else if (dayNum === lastDay) {
            if (daysSinceLast >= 7) return 'PWF'; // Pickup-Work-Finish
            return 'WF'; // Work Finish
          } else if (daysSinceLast >= 7) {
            return 'PW'; // Pickup-Work (back from hiatus)
          } else if (daysUntilNext >= 7) {
            return 'WD'; // Work-Drop (before hiatus)
          } else {
            return 'W'; // Regular Work
          }
        } else {
          // Not working but between first and last day = Hold
          return 'H';
        }
      };

      // 7. Build days array with character statuses
      // Use the shooting days directly - each has its date from the day break
      const days = [];
      const totalDays = shootingDays.length;
      
      console.log('Building DOOD days from shooting days:', shootingDays.length);
      console.log('Initial shooting days with dates:', shootingDays.map(sd => ({ day: sd.day, date: sd.date, isOff: sd.isOff })));
      
      // Simply use the shooting days in order - each day has its date
      shootingDays.forEach((sd, idx) => {
        const dayNum = sd.day || (idx + 1);
        const dayDate = sd.date ? new Date(sd.date) : null;
        
        const characterStatuses = characters
          .map(c => {
            const manualKey = `${c.id}-${dayNum}`;
            const manualStatus = savedEdits.manualStatuses?.[manualKey];
            if (manualStatus) {
              return {
                characterId: c.id,
                status: manualStatus
              };
            }

            // Skip auto statuses on off days so weekends/gap days stay blank
            if (sd.isOff) {
              return null;
            }

            const autoStatus = calculateStatus(c.id, dayNum, totalDays);
            if (!autoStatus) {
              return null;
            }
            return {
              characterId: c.id,
              status: autoStatus
            };
          })
          .filter(Boolean);

        days.push({
          day: dayNum,
          date: sd.date, // Store as YYYY-MM-DD string to preserve local date
          isOff: sd.isOff || false,
          characterStatuses
        });
      });
      
      // Check if we have gaps between dates (weekends, etc.)
      // If dates are available, insert off days for gaps
      console.log('Days before gap detection:', days.map(d => ({ day: d.day, date: d.date, isOff: d.isOff })));
      
      const daysWithDates = days.filter(d => d.date);
      if (daysWithDates.length >= 2) {
        // Sort by date using YYYY-MM-DD string comparison
        daysWithDates.sort((a, b) => {
          if (a.date < b.date) return -1;
          if (a.date > b.date) return 1;
          return 0;
        });
        
        const expandedDays = [];
        for (let i = 0; i < daysWithDates.length; i++) {
          const currentDay = daysWithDates[i];
          expandedDays.push(currentDay);
          
          // Check if there's a gap to the next day
          if (i < daysWithDates.length - 1) {
            const nextDay = daysWithDates[i + 1];
            
            // Parse dates in local time to avoid timezone issues
            const [currYear, currMonth, currDay] = currentDay.date.split('-').map(Number);
            const [nextYear, nextMonth, nextDayVal] = nextDay.date.split('-').map(Number);
            
            const currentDate = new Date(currYear, currMonth - 1, currDay);
            const nextDate = new Date(nextYear, nextMonth - 1, nextDayVal);
            
            // Add off days for each gap day
            const gapDate = new Date(currentDate);
            gapDate.setDate(gapDate.getDate() + 1);
            
            while (gapDate < nextDate) {
              const gapYear = gapDate.getFullYear();
              const gapMonth = String(gapDate.getMonth() + 1).padStart(2, '0');
              const gapDayNum = String(gapDate.getDate()).padStart(2, '0');
              const gapDateStr = `${gapYear}-${gapMonth}-${gapDayNum}`;
              
              expandedDays.push({
                day: null,
                date: gapDateStr,
                isOff: true,
                characterStatuses: []
              });
              gapDate.setDate(gapDate.getDate() + 1);
            }
          }
        }
        
        // Replace days with expanded days (includes gaps)
        days.length = 0;
        expandedDays.forEach(d => days.push(d));
        
        console.log('Days after gap detection:', days.map(d => ({ day: d.day, date: d.date, isOff: d.isOff })));
        
        // Set start date
        if (days.length > 0 && days[0].date) {
          const [y, m, d] = days[0].date.split('-').map(Number);
          startDate = new Date(y, m - 1, d);
        }
      }
      
      // Renumber shooting days sequentially (1, 2, 3, 4, 5...)
      let shootingDayNum = 1;
      days.forEach(day => {
        if (!day.isOff) {
          day.day = shootingDayNum;
          shootingDayNum++;
        }
      });
      
      console.log('Final DOOD days:', days.map(d => ({
        day: d.day,
        date: d.date ? new Date(d.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }) : null,
        isOff: d.isOff
      })));
      
      // DEBUG: Find 2/9 in final days
      const day29 = days.find(d => d.date === '2026-02-09');
      if (day29) {
        console.log('*** FINAL 2/9 STATUS ***', {
          day: day29.day,
          date: day29.date,
          isOff: day29.isOff
        });
      }
      
      // Fallback if no shooting days
      if (days.length === 0) {
        // Fallback: no dates available, use sequential days
        const fallbackTotalDays = shootingDays.length || 10;
        for (let dayNum = 1; dayNum <= fallbackTotalDays; dayNum++) {
          const dayDate = new Date(startDate);
          dayDate.setDate(dayDate.getDate() + dayNum - 1);
          
          const characterStatuses = characters.map(c => {
            // Check for manual status override first
            const manualKey = `${c.id}-${dayNum}`;
            if (savedEdits.manualStatuses && savedEdits.manualStatuses[manualKey]) {
              return {
                characterId: c.id,
                status: savedEdits.manualStatuses[manualKey]
              };
            }
            return {
              characterId: c.id,
              status: calculateStatus(c.id, dayNum, fallbackTotalDays)
            };
          }).filter(cs => cs.status !== null);

          // Check if this day was manually marked as off
          const isManuallyOff = savedEdits.offDays?.includes(dayNum);

          days.push({
            day: dayNum,
            date: dayDate.toISOString().split('T')[0], // Store as YYYY-MM-DD
            isOff: isManuallyOff,
            characterStatuses
          });
        }
      }

      // 8. Create the DOOD schedule with project name
      // Apply saved off days for manually marked days
      const offDays = savedEdits.offDays || [];
      
      const generatedSchedule = {
        name: projectName,
        description: `Day Out of Days for ${projectName}`,
        projectId,
        startDate: startDate.toISOString(),
        totalCharacters: characters.length,
        totalDays: days.filter(d => !d.isOff).length,
        characters,
        days,
        offDays
      };

      generatedSchedule._id = `dood-${projectId}`;
      generatedSchedule.createdAt = new Date().toISOString();
      generatedSchedule.updatedAt = new Date().toISOString();
      
      setSchedule(generatedSchedule);
      setError(null);
      
    } catch (err) {
      console.error('Error generating DOOD schedule:', err);
      setError('No data available to generate Day Out of Days. Please add cast members or upload a screenplay first.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate number of weeks in schedule
  const getNumWeeks = () => {
    if (!schedule?.days) return 1;
    return Math.ceil(schedule.days.length / 5); // 5-day work weeks
  };

  const handlePrintClick = () => {
    if (!schedule) return;
    setOpenPrintDialog(true);
  };

  const handlePrintConfirm = () => {
    setOpenPrintDialog(false);
    handleExportToPDF();
  };

  const handleExportToPDF = () => {
    if (!schedule) return;
    
    // Filter days based on print option
    let daysToprint = schedule.days;
    let dayStartIndex = 0;
    let weekLabel = '';
    
    if (printOption === 'week') {
      const startDay = (selectedWeek - 1) * 5;
      const endDay = startDay + 5;
      daysToprint = schedule.days.slice(startDay, endDay);
      dayStartIndex = startDay;
      weekLabel = ' - Week ' + selectedWeek + ' (Days ' + (startDay + 1) + '-' + Math.min(endDay, schedule.days.length) + ')';
    }
    
    const numDays = daysToprint.length;
    const numChars = schedule.characters.length;
    
    // Adjust font size based on number of days (smaller for more days)
    let fontSize = '10px';
    let cellPadding = '4px 6px';
    let statusFontSize = '9px';
    let statusPadding = '2px 6px';
    
    if (numDays > 30) {
      fontSize = '7px';
      cellPadding = '2px 3px';
      statusFontSize = '6px';
      statusPadding = '1px 3px';
    } else if (numDays > 20) {
      fontSize = '8px';
      cellPadding = '3px 4px';
      statusFontSize = '7px';
      statusPadding = '1px 4px';
    } else if (numDays > 10) {
      fontSize = '9px';
      cellPadding = '3px 5px';
      statusFontSize = '8px';
      statusPadding = '2px 5px';
    }
    
    // Get off days
    const offDays = schedule.offDays || [];
    
    // Build day headers with dates - grey out off days
    const dayHeaders = daysToprint.map((d, i) => {
      const dayNum = dayStartIndex + i + 1;
      const isOff = offDays.includes(dayNum);
      // Format date from d.date (YYYY-MM-DD)
      let dateStr = '';
      if (d.date) {
        const [year, month, day] = d.date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      const headerText = dateStr || dayNum.toString();
      if (isOff) {
        return '<th class="off-day">' + headerText + '<br><small>OFF</small></th>';
      }
      return '<th>' + headerText + '</th>';
    }).join('');
    
    // Build day of week row
    const dayOfWeekRow = daysToprint.map((d, i) => {
      const dayNum = dayStartIndex + i + 1;
      const isOff = offDays.includes(dayNum);
      // Get day of week from d.date
      let dayOfWeek = '';
      if (d.date) {
        const [year, month, day] = d.date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        dayOfWeek = days[dateObj.getDay()];
      }
      const cellClass = isOff ? 'off-day' : '';
      return '<td class="' + cellClass + '">' + dayOfWeek + '</td>';
    }).join('');
    
    // Build shooting day row (shows shooting day number or OFF)
    let shootingDayNum = 1;
    const shootingDayRow = daysToprint.map((d, i) => {
      const dayNum = dayStartIndex + i + 1;
      const isOff = offDays.includes(dayNum) || d.isOff;
      let display = '';
      if (isOff) {
        display = 'OFF';
      } else {
        display = shootingDayNum.toString();
        shootingDayNum++;
      }
      const cellClass = isOff ? 'off-day' : '';
      return '<td class="' + cellClass + '">' + display + '</td>';
    }).join('');
    
    // Build character rows
    const characterRows = schedule.characters.map((char, idx) => {
      const charId = char.id || char._id;
      let workCount = 0, holdCount = 0, totalCount = 0;
      
      const dayCells = daysToprint.map((day, i) => {
        const dayNum = dayStartIndex + i + 1;
        const isOff = offDays.includes(dayNum);
        
        if (isOff) {
          return '<td class="off-day"></td>';
        }
        
        const cs = (day.characterStatuses || []).find(s => s.characterId === charId);
        const status = cs?.status;
        if (status) {
          totalCount++;
          if (['W', 'SW', 'WF', 'SWF', 'WD', 'PW', 'PWF'].includes(status)) workCount++;
          if (status === 'H') holdCount++;
          return '<td><span class="status status-' + status + '">' + status + '</span></td>';
        }
        return '<td></td>';
      }).join('');
      
      const rowClass = idx % 2 === 1 ? 'alt-row' : '';
      return '<tr class="' + rowClass + '">' +
        '<td>' + (idx + 1) + '</td>' +
        '<td class="char-name">' + char.name + '</td>' +
        dayCells +
        '<td class="summary"><strong>' + workCount + '</strong></td>' +
        '<td class="summary"><strong>' + holdCount + '</strong></td>' +
        '<td class="summary"><strong>' + totalCount + '</strong></td>' +
        '</tr>';
    }).join('');
    
    // Build print-friendly HTML with responsive sizing
    const title = schedule.name || projectName;
    const printContent = '<!DOCTYPE html><html><head>' +
      '<title>Day Out of Days - ' + title + '</title>' +
      '<style>' +
      '* { box-sizing: border-box; margin: 0; padding: 0; }' +
      'body { font-family: Arial, sans-serif; font-size: ' + fontSize + '; padding: 10px; }' +
      'table { border-collapse: collapse; width: 100%; table-layout: fixed; }' +
      'th, td { border: 1px solid #333; padding: ' + cellPadding + '; text-align: center; overflow: hidden; }' +
      'th { background-color: #2d5a6b; color: white; font-weight: bold; }' +
      '.char-name { text-align: left; font-weight: bold; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100px; }' +
      '.summary { background-color: #e8e8e8; }' +
      '.status { display: inline-block; padding: ' + statusPadding + '; border-radius: 2px; font-weight: bold; font-size: ' + statusFontSize + '; }' +
      '.status-SW, .status-W, .status-WF { background: #4caf50; color: white; }' +
      '.status-SWF { background: #8bc34a; color: white; }' +
      '.status-H { background: #ff9800; color: white; }' +
      '.status-I { background: #ffeb3b; color: #333; }' +
      '.status-T { background: #2196f3; color: white; }' +
      '.status-R, .status-SR { background: #9c27b0; color: white; }' +
      '.status-WD { background: #f44336; color: white; }' +
      '.status-PW, .status-PWF { background: #00bcd4; color: white; }' +
      '.header { text-align: center; margin-bottom: 10px; }' +
      '.header h1 { font-size: 14px; margin-bottom: 3px; }' +
      '.header h2 { font-size: 11px; color: #666; }' +
      '.header .stats { font-size: 9px; color: #888; margin-top: 3px; }' +
      '.alt-row { background-color: #f5f5f5; }' +
      '.off-day { background-color: #ccc !important; color: #666 !important; }' +
      '.day-header-row th { font-size: ' + (numDays > 20 ? '7px' : '9px') + '; }' +
      '@media print { ' +
      '  body { padding: 5px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
      '  @page { size: landscape; margin: 0.25in; }' +
      '  thead { display: table-header-group; }' +
      '  tr { page-break-inside: avoid; }' +
      '}' +
      '</style></head><body>' +
      '<div class="header">' +
      '<h1>DAY OUT OF DAYS REPORT</h1>' +
      '<h2>' + title.toUpperCase() + weekLabel + '</h2>' +
      '<div class="stats">' + numChars + ' Characters | ' + numDays + ' Shooting Days' + (printOption === 'week' ? ' (of ' + schedule.days.length + ' total)' : '') + '</div>' +
      '</div>' +
      '<table><thead class="day-header-row"><tr>' +
      '<th style="width:20px">#</th>' +
      '<th class="char-name" style="width:100px">Character</th>' +
      dayHeaders +
      '<th style="width:25px">W</th><th style="width:25px">H</th><th style="width:25px">T</th>' +
      '</tr><tr style="background-color:#f0f0f0;font-size:0.9em;">' +
      '<td></td><td style="text-align:left;font-weight:bold;">Day</td>' +
      dayOfWeekRow +
      '<td></td><td></td><td></td>' +
      '</tr><tr style="background-color:#e8e8e8;font-weight:bold;">' +
      '<td></td><td style="text-align:left;">Shoot</td>' +
      shootingDayRow +
      '<td></td><td></td><td></td>' +
      '</tr></thead><tbody>' +
      characterRows +
      '</tbody></table>' +
      '<div style="margin-top:10px;font-size:8px;color:#666;">' +
      'Legend: SW=Start Work, W=Work, WF=Work Finish, SWF=Start-Work-Finish, H=Hold, WD=Work-Drop, PW=Pickup-Work, PWF=Pickup-Work-Finish' +
      '</div>' +
      '</body></html>';
    
    // Use hidden iframe for printing (no popup)
    let printFrame = document.getElementById('dood-print-frame');
    if (!printFrame) {
      printFrame = document.createElement('iframe');
      printFrame.id = 'dood-print-frame';
      printFrame.style.position = 'absolute';
      printFrame.style.top = '-10000px';
      printFrame.style.left = '-10000px';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      document.body.appendChild(printFrame);
    }
    
    const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
    const doc = frameDoc.document || frameDoc;
    
    doc.open();
    doc.write(printContent);
    doc.close();
    
    // Wait for content to load then print
    printFrame.onload = () => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    };
  };

  // Handle schedule updates from the report (when user edits a status or toggles off days)
  const handleScheduleUpdate = (updatedSchedule, editType, editData) => {
    // Update local state immediately
    updatedSchedule.updatedAt = new Date().toISOString();
    setSchedule(updatedSchedule);
    
    // Save manual edits to localStorage so they persist across regenerations
    const currentEdits = loadManualEdits();
    
    if (editType === 'offDay') {
      // editData = { dayNum, isOff }
      if (editData.isOff) {
        if (!currentEdits.offDays.includes(editData.dayNum)) {
          currentEdits.offDays.push(editData.dayNum);
        }
      } else {
        currentEdits.offDays = currentEdits.offDays.filter(d => d !== editData.dayNum);
      }
    } else if (editType === 'status') {
      // editData = { characterId, dayNum, status }
      const key = `${editData.characterId}-${editData.dayNum}`;
      if (editData.status === null) {
        delete currentEdits.manualStatuses[key];
      } else {
        if (!currentEdits.manualStatuses) currentEdits.manualStatuses = {};
        currentEdits.manualStatuses[key] = editData.status;
      }
    }
    
    saveManualEdits(currentEdits.offDays, currentEdits.manualStatuses);
  };

  // Render empty state when no schedule and no error
  const renderEmptyState = () => (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 4, 
        textAlign: 'center', 
        bgcolor: 'grey.100',
        borderRadius: 1 
      }}
    >
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Day Out of Days Data Available
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Add cast members, upload a screenplay, or create scenes in the One-Liner to generate the Day Out of Days report.
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ pl: 2 }}>
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">{projectName} - Day Out of Days</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={generateSchedule}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              localStorage.removeItem(`dood-edits-${projectId}`);
              window.location.reload();
            }}
            disabled={!schedule || loading}
            size="small"
          >
            Clear Saved Edits
          </Button>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handlePrintClick}
            disabled={!schedule || loading}
            size="small"
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Schedule Stats */}
      {schedule && !loading && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={`${schedule.totalCharacters || 0} Characters`} 
            size="small" 
            icon={<PersonIcon />}
          />
          <Chip 
            label={`${schedule.totalDays || 0} Shooting Days`} 
            size="small"
          />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
            Generated: {schedule.createdAt ? new Date(schedule.createdAt).toLocaleDateString() : 'N/A'}
          </Typography>
        </Box>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Main content */}
      {!loading && (
        <>
          {schedule ? (
            <DayOutOfDaysReport 
              schedule={schedule}
              projectName={projectName}
              onScheduleUpdate={handleScheduleUpdate}
            />
          ) : (
            renderEmptyState()
          )}
        </>
      )}

      {/* Print Options Dialog */}
      <Dialog open={openPrintDialog} onClose={() => setOpenPrintDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Print Options</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl component="fieldset">
              <RadioGroup
                value={printOption}
                onChange={(e) => setPrintOption(e.target.value)}
              >
                <FormControlLabel 
                  value="all" 
                  control={<Radio />} 
                  label={`Entire Schedule (${schedule?.days?.length || 0} days)`}
                />
                <FormControlLabel 
                  value="week" 
                  control={<Radio />} 
                  label="Specific Week (5 days)"
                />
              </RadioGroup>
            </FormControl>

            {printOption === 'week' && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Week</InputLabel>
                <Select
                  value={selectedWeek}
                  label="Select Week"
                  onChange={(e) => setSelectedWeek(e.target.value)}
                >
                  {Array.from({ length: getNumWeeks() }, (_, i) => {
                    const startDay = i * 5 + 1;
                    const endDay = Math.min((i + 1) * 5, schedule?.days?.length || 0);
                    return (
                      <MenuItem key={i + 1} value={i + 1}>
                        Week {i + 1} (Days {startDay}-{endDay})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrintDialog(false)}>Cancel</Button>
          <Button onClick={handlePrintConfirm} color="primary" variant="contained" startIcon={<PdfIcon />}>
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DayOutOfDaysTab;
